import React, { memo, useCallback } from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { useForm } from "react-hook-form";
import { FormControl, FormLabel, Input, Stack, Text } from "@chakra-ui/react";
import { NodeType } from "../../../models/nodeType";
import { FlowNodeWithChildren } from "./FlowNode";

interface FormData {
  npcIdsText: string;
}

type NodeDataShape = {
  npcIds?: string[];
};

function parseNpcIds(text: string): string[] {
  return text
    .split(/[,\n\t ]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

const NpcWanderingModeStopNode: React.FC<NodeProps<NodeType>> = (props) => {
  const {
    id: nodeId,
    data: { color, nodeData = [] },
  } = props;

  const formData = nodeData as NodeDataShape | undefined;
  const npcIds = formData?.npcIds ?? [];

  const { register, handleSubmit } = useForm<FormData>({
    defaultValues: {
      npcIdsText: npcIds.join(","),
    },
    mode: "onChange",
  });

  const reactFlow = useReactFlow();

  const handleUpdate = useCallback(
    ({ npcIdsText }: FormData) => {
      reactFlow.setNodes((nodes) => {
        const node = nodes.find(({ id }) => id === nodeId);
        if (node == null) return nodes;

        const nodeDataCloned = structuredClone(node.data) as NodeType;
        const next = (nodeDataCloned.nodeData as NodeDataShape) ?? {};

        next.npcIds = parseNpcIds(npcIdsText);

        node.data = {
          ...nodeDataCloned,
          nodeData: next,
        };

        return nodes;
      });
    },
    [reactFlow, nodeId]
  );

  return (
    <FlowNodeWithChildren {...props}>
      <form className={"nodrag"} onSubmit={handleSubmit(handleUpdate)} onBlur={handleSubmit(handleUpdate)}>
        <Stack spacing={3}>
          <FormControl>
            <FormLabel>
              <Text casing={"uppercase"} color={color}>
                NPC IDs (comma/space separated)
              </Text>
            </FormLabel>
            <Input {...register("npcIdsText")} color={color} placeholder={"npc_foo,npc_bar"} />
          </FormControl>

          <Text fontSize={"sm"} color={color} opacity={0.8}>
            Stops wandering immediately (v1).
          </Text>
        </Stack>
      </form>
    </FlowNodeWithChildren>
  );
};

export default memo(NpcWanderingModeStopNode);
